import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AutoSub, AutoSubOnDestroy, DatabaseService, MoteService, ProjectService, QuizService, StatsService, UserService } from '../../providers';
import { Challenge, Mote, MotesPaths, StatEvent, StatType } from '../../models';
import { AnswerDistribution, QuizOverview, QuizStat } from './quiz-stats-helpers';

@AutoSubOnDestroy()
@Component({
    selector: 'quiz-stats',
    templateUrl: './quiz-stats.component.html',
    styleUrls: ['./quiz-stats.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
/**
 * UI for displaying statistics for challenges
 */
export class QuizStatsComponent implements OnInit {
    // DATA
    /**  Storing all `Mote` objects from the currently selected project */
    private projectMotes: Mote[] = [];
    /** Storing all `Challenge` objects from the currently selected project */
    public challenges: Challenge[] = [];
    /** Casting the currently selected `Challenge` */
    private activeChallengeSubject: ReplaySubject<Challenge> = new ReplaySubject<Challenge>();
    /** Storing the currently selected `Challenge` */
    public activeChallenge: Challenge = null;
    /** Quiz statistics for the currently selected `Challenge` */
    public quizStats: QuizStat[] = [];
    /** Quiz statistics (from StatsService) for wrong and right answers, respectively, for the currently selected `Challenge` */
    public answerDistributionList: AnswerDistribution[] = [];
    /** Object containing aggregated data for the active challenge */
    public quizOverview: QuizOverview = null;
    /** Casting the currently selected `QuizStat` (user from the table of participants) */
    private activeQuizStatSubject: ReplaySubject<QuizStat> = new ReplaySubject<QuizStat>();
    /** Storing the currently selected `QuizStat` */
    public activeQuizStat: QuizStat = null;

    // SORTING
    /** Flag to determine sort direction for entries in table of participants */
    public sortForward: boolean = false;
    /** Variable storing the string the table of participants is currently being sorted by */
    public currentSortString: string = 'rank';

    // LOADING
    /** Flag to determine whether the overview is loading */
    public overviewLoading: boolean = true;

    constructor(
        private projectService: ProjectService,
        private quizService: QuizService,
        private db: DatabaseService,
        private userService: UserService,
        private moteService: MoteService,
        private statsService: StatsService,
        private router: Router
    ) { }

    ngOnInit(): void {
        // Subscribe to (published) challenges from the active project
        this.watchChallenges();

        // Populate answer distribution with right and wrong quiz answers taken from StatsService
        this.populateAnswerDistribution();

        // Subscribe to users (and motes) from the active challenge and update statistics accordingly   
        this.generateQuizStats();

        // Subscribe to changes in the active quiz stat (based on user interaction) and update member variable
        this.watchActiveQuizStat();
    }

    /**
     * Subscribe to changes in the active `QuizStat` and update the member variable controlling the view accordingly.
     * The changes are cast when the user clicks on an entry in the participants table.
     */
    private watchActiveQuizStat() {
        AutoSub(this).reg['activeQuizStatSub'] = this.activeQuizStatSubject.subscribe((quizStat) => {
            // If the currently active quiz stat is pushed again, deselect it by setting the member variable to null
            if(this.activeQuizStat == quizStat) return (this.activeQuizStat = null);
            // Otherwise, set the member variable to the new quiz stat
            return (this.activeQuizStat = quizStat);
        });
    }

    /**
     * Watch users (and motes) associated with the currently active `Challenge` and generate statistics based on their performance
     */
    private generateQuizStats(): void {
        // Observable watching motes from the current project
        const watchProjectMotes = this.projectService.watchActiveProject().pipe(
            // Watch motes from the current project
            switchMap((project) => this.moteService.watchProjectMotes([project.project.key])),
            // Update member variable with incoming motes
            tap((motes) => this.projectMotes = motes)
        );

        // Observable watching users from the currently selected challenge
        const watchChallengeUsers = this.activeChallengeSubject.pipe(
            // Update the member variable storing the currently active challenge
            tap((challenge) => this.activeChallenge = challenge),
            // Get keys for users who have participated in the challenge
            switchMap((challenge) => challenge ? this.watchChallengeUsers(challenge.key) : of([])),
            // Watch the `User` objects for the provided keys
            switchMap((userKeys) => this.userService.watchUsersByIds(userKeys, true))
        );

        // Generate quiz stats when both project motes and challenge users are available
        AutoSub(this).reg['quizStatSub'] = watchProjectMotes.pipe(switchMap(() => watchChallengeUsers))
            .subscribe((users) => {
                // Filter out admins and super admins. Generate `QuizStat`objects for the remaining users and store them in an array
                let quizStats = users.filter((user) => !(user.isAdmin || user.isSuperAdmin)).map((user) => new QuizStat(user, this.activeChallenge.key, this.projectMotes));
                // Sort array based on points (highest to lowest) and add rank to objects
                quizStats
                    .sort((a, b) => b.points - a.points)
                    .forEach((stat, index) => (stat.rank = index + 1));

                // Store reference to array in member variable
                this.quizStats = quizStats;
                // Generate a quiz overview and store a reference in a member variable
                this.quizOverview = this.generateQuizOverview(quizStats);

                // Update the active `QuizStat` if new user data is pushed from the database
                if(this.activeQuizStat) {
                    this.activeQuizStat = quizStats.find((quizStat) => quizStat.user.key == this.activeQuizStat.user.key);
                }
            });
    }

    /**
     * Watch the `Challenge` objects of the current project and update the member variables controlling the view accordingly
     */
    private watchChallenges(): void {
        AutoSub(this).reg['challengeSub'] = this.projectService.watchActiveProject().pipe(
            // Reset the state of the view on project change
            tap(() => this.resetState()),
            // Watch the challenges for the current project (including inactive)
            switchMap((activeProject) => this.quizService.watchProjectChallenges(activeProject.project.key, false))
        ).subscribe((challenges) => {
            // If the project has no associated challenges, reset the state of the variables controlling the view
            if(challenges.length <= 0) return this.resetState();

            // If the project has associated challenges, store them in a member variable
            this.challenges = challenges;
            // Automatically select the first challenge from the project
            this.setActiveChallenge(challenges[0]);
        });
    }

    /**
     * Combine quiz statistics from `StatsService` with `Mote` objects from the active project to populate the `answerDistribution` member variable
     */
    private populateAnswerDistribution(): void {
        // Watch right quiz answers for the active project
        const rightAnswers = this.projectService.watchActiveProject().pipe(switchMap((project) => this.statsService.watchStatType(0, new Date().getTime(), StatType.QUIZ_ANSWER_RIGHT, project.project.key)));
        // Watch wrong quiz answers for the active project
        const wrongAnswers = this.projectService.watchActiveProject().pipe(switchMap((project) => this.statsService.watchStatType(0, new Date().getTime(), StatType.QUIZ_ANSWER_WRONG, project.project.key)));

        // Create a stream of combined quiz answers from the database for the currently selected project
        let watchQuizStats = this.projectService.watchActiveProject().pipe(
            // Set loading flag
            tap(() => this.overviewLoading = true),
            // Watch motes from the current project
            switchMap((project) => this.moteService.watchProjectMotes([project.project.key])),
            // Update member variable with incoming motes
            tap((motes) => this.projectMotes = motes),
            // Combine right and wrong quiz answers from the active project
            switchMap(() => combineLatest([rightAnswers, wrongAnswers]))
        );

        // Combine the quiz answers with motes from the current project in a `AnswerDistribution` object
        AutoSub(this).reg['answerDistributionSub'] = watchQuizStats.subscribe((stats) => {
            // Concat the two streams of quiz answers
            let combinedStats = [].concat(...stats) as StatEvent[];

            // Initialize mapped type for storing AnswerDistribution objects
            let answerDistributionMap: { [questionKey: string]: AnswerDistribution; } = {};

            // Process stats
            for(let stat of combinedStats) {
                // Add the object if it does not exist in the map
                if(!answerDistributionMap[stat.moteKey]) {
                    answerDistributionMap[stat.moteKey] = new AnswerDistribution(stat.moteKey, this.projectMotes.find((mote) => mote.key == stat.moteKey));
                }

                // Increment the property corresponding to the `StatType`
                stat.statType == StatType.QUIZ_ANSWER_RIGHT ? answerDistributionMap[stat.moteKey].right++ : answerDistributionMap[stat.moteKey].wrong++;
            }

            // Calculate percentage of right answers
            for(let object in answerDistributionMap) {
                let objVal = answerDistributionMap[object];
                objVal.percentageTrue = (objVal.right / (objVal.right + objVal.wrong)) * 100;
            }

            // Convert map to array and sort based on mote order
            this.answerDistributionList = Object.values(answerDistributionMap).sort((a, b) => {
                // If the objects are undefined, set a high default value to make the entry appear last in the list
                let aOrder = a.mote ? a.mote.order : Number.MAX_VALUE;
                let bOrder = b.mote ? b.mote.order : Number.MAX_VALUE;

                return aOrder - bOrder;
            });
            // Set loading flag
            this.overviewLoading = false;
        });
    }

    /**
     * Generate a `QuizOverview` object for the currently selected challenge
     * @param quizStats Array containing all quiz stats for the currently selected challenge
     * @returns A `QuizOverview` object containing general data for the currently selected challenge
     */
    private generateQuizOverview(quizStats: QuizStat[]): QuizOverview {
        // Filter out durations greater than 24h
        let filteredQuizStats = quizStats.filter((stat) => stat.duration < 60 * 60 * 24);

        return {
            participants: quizStats.length,
            averagePoints: quizStats.reduce((acc, stat) => acc + stat.points, 0) / quizStats.length,
            averageDuration: filteredQuizStats.reduce((acc, stat) => acc + stat.duration, 0) / filteredQuizStats.length,
        };
    }

    /**
     * Watch all users from a certain `Challenge`
     * @param challengeKey Key for the `Challenge`
     * @returns An `Observable<string[]>` with user data from the database
     */
    private watchChallengeUsers(challengeKey: string): Observable<string[]> {
        let query = (ref) => ref.orderByChild('challengeKey').equalTo(challengeKey);

        return this.db
            .list<{ userKey: string; challengeKey: string; }>(MotesPaths.USER_CHALLENGE_LINK_DB_PATH(), query)
            .valueChanges()
            .pipe(map((userChallengeLinks) => userChallengeLinks.map((link) => link.userKey)));
    }

    /**
     * View statistics for the selected `Challenge`
     * @param challenge Selected `Challenge`
     */
    public setActiveChallenge(challenge: Challenge): void {
        this.activeChallengeSubject.next(challenge);
    }

    /**
     * View detailed statistics for the selected user (`QuizStat`).
     * @param quizStat The selected `QuizStat`
     */
    public setActiveQuizStat(quizStat: QuizStat): void {
        this.activeQuizStatSubject.next(quizStat);
    }

    /**
     * Sort a table by a field with a `number` value
     * @param fieldName name of the field to sort the data by
     */
    public sortByNumber(fieldName: 'rank' | 'points' | 'level'): void {
        this.currentSortString = fieldName;
        this.quizStats = this.quizStats.sort((a, b) => (this.sortForward ? a[fieldName] - b[fieldName] : b[fieldName] - a[fieldName]));
        this.sortForward = !this.sortForward;
    };

    /**
     * Sort a table by a field with a `string` value
     * @param fieldName name of the field to sort the data by
     */
    public sortByString(fieldName: 'userName' | 'email'): void {
        this.currentSortString = fieldName;
        this.quizStats = this.quizStats.sort((a, b) => (this.sortForward ? a[fieldName].localeCompare(b[fieldName]) : b[fieldName].localeCompare(a[fieldName])));
        this.sortForward = !this.sortForward;
    }

    /**
     * Reset the state of the view
     */
    private resetState(): void {
        this.setActiveChallenge(null);
        this.challenges = [];

        this.setActiveQuizStat(null);
        this.quizStats = [];

        this.quizOverview = null;
        this.answerDistributionList = [];
    }
}
